import Bugsnag from "@bugsnag/js";
import produce from "immer";
import findKey from "lodash/findKey";
import React, { FunctionComponent } from "react";
import { FormattedNumber, useIntl } from "react-intl";
import CartRadioInputWithLabel from "@Components/molecules/cart/CartRadioInputWithLabel";
import useCartContext from "@Hooks/useCartContext";
import { ShippingOption } from "@Types/addresses";
import CheerzApiCart from "@Types/api/cart";
import { ApiErrorResponse } from "@Types/api/error";
import { PriceFormatted } from "@Types/price";
import { convertCartFromApiToContent } from "@Utils/cart/cart";
import { formatDates, getDeliveryMethods } from "@Utils/cart/deliveryMethods";
import { convertShippingOptionsFromContentToApi } from "@Utils/cart/deliveryMethods";
import { convertToOrderUpdatedProperties, setDataLayerTrack } from "@Utils/track";
import "./style.scss";

interface Props {
  deliveryInfos: Array<{
    deliveryDates?: string[];
    price?: PriceFormatted;
    selected: boolean;
    type: string;
  }>;
  onChange: (val: string) => void;
}

export const CartDeliveryContent: FunctionComponent<Props> = ({ deliveryInfos, onChange }) => {
  const intl = useIntl();

  return (
    <div className="CartDeliveryContent">
      {deliveryInfos.map(({ deliveryDates, price, selected, type }, key) => (
        <div className="CartDeliveryContent-choice" key={key}>
          <CartRadioInputWithLabel
            checked={selected}
            name="shipping"
            onChange={(val) => onChange(val)}
            value={type}
          >
            <div className="CartDeliveryContent-infos">
              <div className="CartDeliveryContent-infos-description">
                {deliveryDates && (
                  <div className="CartDeliveryContent-infos-description-deliveryDates">
                    {formatDates(deliveryDates[0], deliveryDates[1], intl.locale)}
                  </div>
                )}
                <div className="CartDeliveryContent-infos-description-deliveryType">
                  {intl.formatMessage({
                    id: `cheerz_CartDeliveryContent_${type}`,
                  })}
                </div>
              </div>
              {price && (
                <div className="CartDeliveryContent-infos-price">
                  <FormattedNumber
                    currency={price.currency}
                    style="currency"
                    value={price.cents / 100}
                  />
                </div>
              )}
            </div>
          </CartRadioInputWithLabel>
        </div>
      ))}
    </div>
  );
};

const CartDeliveryContentContainer: FunctionComponent = () => {
  const { cart, configuration, dispatch } = useCartContext();

  const deliveryMethod = getDeliveryMethods(cart).find((method) => method.selected);
  const deliveryModes = deliveryMethod && deliveryMethod.deliveryModes;

  const deliveryInfos =
    deliveryModes &&
    Object.entries(deliveryModes)
      .reverse()
      .filter(([, mode]: [string, ShippingOption]) => mode.available)
      .map(([key, mode]: [string, ShippingOption]) => ({
        deliveryDates: mode.deliveryDates,
        price: mode.price,
        selected: mode.selected,
        type: key,
      }));

  const handleChangeDelivery = (mode: string) => {
    if (mode !== "express" && mode !== "normal") {
      return;
    }
    const currentOption = findKey(cart.shippingOptions, (option) => {
      return option !== undefined && (option.normal.selected || option.express.selected);
    });
    if (currentOption === "home" || currentOption === "mondialRelay") {
      dispatch({
        mode,
        option: currentOption,
        type: "setDeliveryMode",
      });

      const updatedCart = produce(cart, (draftState) => {
        const cartOption = draftState.shippingOptions[currentOption];
        if (!cartOption) {
          return;
        }
        cartOption.express.selected = mode === "express";
        cartOption.normal.selected = mode === "normal";
      });

      const shippingOptions = updatedCart.shippingOptions[currentOption];

      if (shippingOptions !== undefined) {
        dispatch({
          disabled: true,
          type: "setDisabledCart",
        });
        const shippingOptionsJson = convertShippingOptionsFromContentToApi(shippingOptions);

        configuration.apiProviders.cheerz
          .changeShippingOption(shippingOptionsJson)
          .then((cartResponse: CheerzApiCart) => {
            dispatch({
              cart: {
                ...convertCartFromApiToContent(cartResponse),
              },
              type: "setSavedCart",
            });
            setDataLayerTrack("Order Updated", convertToOrderUpdatedProperties(cart, "Overview"));
          })
          .catch((error: ApiErrorResponse) => {
            dispatch({
              disabled: false,
              type: "setDisabledCart",
            });
            Bugsnag.notify(
              Error(
                error.response?.data?.error_description[0]
                  ? `${error.response?.data?.error_description[0]}`
                  : "Error on changeShippingOption call"
              )
            );
          });
      }
    }
  };

  return deliveryInfos ? (
    <CartDeliveryContent deliveryInfos={deliveryInfos} onChange={handleChangeDelivery} />
  ) : null;
};

export default CartDeliveryContentContainer;
